import * as THREE from "three";
import { MAX_PARTICLES } from "../core/config";
import { SeededRandom, hashNumbers } from "../core/random";

export class ParticleBurst {
  readonly points: THREE.Points;

  private readonly positions = new Float32Array(MAX_PARTICLES * 3);
  private readonly colors = new Float32Array(MAX_PARTICLES * 3);
  private readonly velocities = new Float32Array(MAX_PARTICLES * 3);
  private readonly life = new Float32Array(MAX_PARTICLES);
  private readonly geometry = new THREE.BufferGeometry();
  private readonly material = new THREE.PointsMaterial({
    size: 0.12,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.9,
    depthWrite: false,
    vertexColors: true,
  });
  private cursor = 0;

  constructor() {
    const positionAttribute = new THREE.BufferAttribute(this.positions, 3);
    const colorAttribute = new THREE.BufferAttribute(this.colors, 3);
    positionAttribute.setUsage(THREE.DynamicDrawUsage);
    colorAttribute.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute("position", positionAttribute);
    this.geometry.setAttribute("color", colorAttribute);
    this.geometry.setDrawRange(0, MAX_PARTICLES);
    this.points = new THREE.Points(this.geometry, this.material);
    this.points.name = "pickup-particles";
    this.points.frustumCulled = false;
    this.positions.fill(-10_000);
  }

  emit(id: number, radiusLocal: number, colorHex: number, count = 9): void {
    const random = new SeededRandom(hashNumbers(id, this.cursor, 0xba_11));
    const color = new THREE.Color(colorHex);
    for (let particle = 0; particle < count; particle += 1) {
      const index = this.cursor;
      this.cursor = (this.cursor + 1) % MAX_PARTICLES;
      const offset = index * 3;
      const angle = random.range(0, Math.PI * 2);
      const speed = random.range(1.1, 3.2) * Math.max(0.5, radiusLocal);
      this.positions[offset] = Math.cos(angle) * radiusLocal * 0.6;
      this.positions[offset + 1] = radiusLocal * random.range(0.45, 1.5);
      this.positions[offset + 2] = Math.sin(angle) * radiusLocal * 0.6;
      this.velocities[offset] = Math.cos(angle) * speed;
      this.velocities[offset + 1] = random.range(1.2, 3.1) * radiusLocal;
      this.velocities[offset + 2] = Math.sin(angle) * speed;
      this.life[index] = random.range(0.42, 0.82);
      this.colors[offset] = color.r;
      this.colors[offset + 1] = color.g;
      this.colors[offset + 2] = color.b;
    }
    this.markDirty();
  }

  update(deltaSeconds: number): void {
    for (let index = 0; index < MAX_PARTICLES; index += 1) {
      if ((this.life[index] ?? 0) <= 0) {
        continue;
      }
      const offset = index * 3;
      this.life[index] = (this.life[index] ?? 0) - deltaSeconds;
      if ((this.life[index] ?? 0) <= 0) {
        this.positions[offset] = -10_000;
        this.positions[offset + 1] = -10_000;
        this.positions[offset + 2] = -10_000;
        continue;
      }
      this.velocities[offset + 1] = (this.velocities[offset + 1] ?? 0) - deltaSeconds * 4.2;
      this.positions[offset] = (this.positions[offset] ?? 0) + (this.velocities[offset] ?? 0) * deltaSeconds;
      this.positions[offset + 1] = (this.positions[offset + 1] ?? 0) + (this.velocities[offset + 1] ?? 0) * deltaSeconds;
      this.positions[offset + 2] = (this.positions[offset + 2] ?? 0) + (this.velocities[offset + 2] ?? 0) * deltaSeconds;
    }
    this.markDirty();
  }

  clear(): void {
    this.life.fill(0);
    this.positions.fill(-10_000);
    this.markDirty();
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }

  private markDirty(): void {
    const positionAttribute = this.geometry.getAttribute("position");
    const colorAttribute = this.geometry.getAttribute("color");
    positionAttribute.needsUpdate = true;
    colorAttribute.needsUpdate = true;
  }
}
